/* tslint:disable:interface-name object-literal-sort-keys */

import { http, apiCall } from '@/api/http'

export type StarterPackScenarioId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 12 | 13 | 14 | 15;

export type StarterPackScenarioRole = "admin" | "master" | "manager";

export type StarterPackScenarioSection = "must" | "quickstart-admin" | "quickstart-master";

export type StarterPackScenarioAction =
    | "setBattleBotMode"
    | "applyDeauthContacts"
    | "applyCommControl"
    | "copyActivation"
    | "redirect";

export type StarterPackScenarioStatus = "inactive" | "active";

export interface StarterPackScenario {
    scenarioId: StarterPackScenarioId;
    name: string;
    role: StarterPackScenarioRole;
    section: StarterPackScenarioSection;
    action: StarterPackScenarioAction;
    automail?: boolean;
    status: StarterPackScenarioStatus;
    configured: boolean;
    settings: Record<string, unknown> | null;
}

export interface StarterPackStatusResponse {
    scenarios: StarterPackScenario[];
    doctorReport?: StarterPackDoctorReport;
    diagnostics?: StarterPackDiagnostic[];
}

export interface StarterPackLaunchResultItem {
    scenarioId: StarterPackScenarioId;
    success: boolean;
    skipped?: boolean;
    error?: string;
}

export interface StarterPackLaunchResponse {
    results: StarterPackLaunchResultItem[];
    launchId: number | null;
    successCount: number;
    errorCount: number;
    doctorReport?: StarterPackDoctorReport;
    diagnostics?: StarterPackDiagnostic[];
}

export interface StarterPackLaunchRequest {
    projectId: string;
    scenarioIds: StarterPackScenarioId[];
    settings: Partial<Record<StarterPackScenarioId, Record<string, unknown>>>;
}

export interface StarterPackDisableResponse {
    scenarioId: number;
    success: boolean;
}

export async function getStarterPackStatus({ projectId }: { projectId: string }) {
    const url = `starterpak/status?projectId=${projectId}`
    return apiCall<StarterPackStatusResponse>(() => http.get(url))
}

export async function launchStarterPackScenarios(payload: StarterPackLaunchRequest) {
    const url = 'starterpak/launch'
    return apiCall<StarterPackLaunchResponse>(() => http.post(url, { json: payload }))
}

export async function disableStarterPackScenario({
    projectId,
    scenarioId,
}: {
    projectId: string;
    scenarioId: StarterPackScenarioId;
}) {
    const url = 'starterpak/disable'
    return apiCall<StarterPackDisableResponse>(() =>
        http.post(url, { json: { projectId, scenarioId } }),
    )
}

export interface StarterPackScenarioMeta {
    scenarioId: StarterPackScenarioId;
    icon: string;
    description: string;
    settingsType?: "battle" | "deauth" | "comm" | "script" | "master15" | "redirect-info" | "cascade";
    redirectUrl?: string;
    redirectAnchor?: string;
    redirectPath?: string;
    drawerDescription?: string;
    drawerSectionTitle?: string;
    drawerSectionDescription?: string;
    drawerLinkLabel?: string;
    vars?: string[];
    manualNote?: string;
    noEmployeePicker?: boolean;
    scriptOnly?: boolean;
}

export interface StarterPackDoctorMetric {
    title: string;
    status: "good" | "bad" | "warn";
    value: string;
    standard: string;
    note: string;
}

export interface StarterPackDiagnostic {
    id: string;
    title: string;
    value: string | null;
    numericValue: number | null;
    standard: string;
    benchmark: {
        operator: string;
        value: number;
    };
    status: "good" | "bad" | "warn" | "unknown";
    note: string;
}

export interface StarterPackDoctorReport {
    growth: string;
    newCountByMonth: number;
    recOtherDay: string;
    conversion2: string;
    conversion3: string;
    returnRate: string;
    productsPercent: string;
    itemsCount: number;
}

export function mapDiagnosticsToDoctorMetrics(
    diagnostics?: StarterPackDiagnostic[],
): StarterPackDoctorMetric[] {
    if (!Array.isArray(diagnostics)) {
        return []
    }

    return diagnostics.filter(isDisplayableDiagnostic).map(item => ({
        title: item.title,
        status: item.status,
        value: item.value,
        standard: item.standard,
        note: item.note,
    }))
}

function isDisplayableDiagnostic(
    item: StarterPackDiagnostic,
): item is StarterPackDiagnostic & { status: 'good' | 'bad' | 'warn'; value: string } {
    return (
        item.value != null &&
        item.value !== '' &&
        (item.status === 'good' || item.status === 'bad' || item.status === 'warn')
    )
}

export interface StarterPackInfoCard {
    icon: string;
    name: string;
    description: string;
    pro?: boolean;
    noLearn?: boolean;
    learnPath?: string;
    marketplaceModule?: keyof typeof import("@root/config.js").default.marketplaceModulesIds.TEST;
}

export interface StarterPackService {
    id: string;
    name: string;
    category: string;
}

export interface StarterPackStaff {
    id: string;
    name: string;
}

export interface StarterPackCase {
    metric: string;
    up: string;
    was: string;
    now: string;
    bars: number[];
    caption: string;
}

export interface StarterPackReview {
    initials: string;
    avatarColor: string;
    name: string;
    outbound?: string;
    outboundTime?: string;
    inbound: string;
    inboundTime: string;
    tag: string;
    heart?: boolean;
}

export function getCaseCategory(metric: string): "conv" | "rev" | "seg" {
    if (/выручк/i.test(metric)) {
        return "rev";
    }
    if (/сегмент/i.test(metric)) {
        return "seg";
    }
    return "conv";
}

export function getCaseAccentColor(caption: string): string {
    if (/Клиника/i.test(caption)) {
        return "#2F6FE0";
    }
    if (/Ногтев/i.test(caption)) {
        return "#E0498B";
    }
    if (/Барбершоп/i.test(caption)) {
        return "#C77F2E";
    }
    return "#7B57D4";
}

export function getCaseBusinessInitials(caption: string): string {
    const match = caption.match(/«([^»]+)»/);
    if (!match) {
        return "••";
    }
    return match[1].replace(/[^A-Za-zА-Яа-яЁё]/g, "").slice(0, 2).toUpperCase();
}

export function getCaseCategoryLabel(metric: string): string {
    const category = getCaseCategory(metric);
    if (category === "rev") {
        return "Выручка";
    }
    if (category === "seg") {
        return "Сегмент";
    }
    return "Конверсия";
}

export function getCaseCategoryIcon(metric: string): string {
    const category = getCaseCategory(metric);
    if (category === "rev") {
        return "mdi-chart-line";
    }
    if (category === "seg") {
        return "mdi-chart-donut-variant";
    }
    return "mdi-sync";
}

export function parseCasePercent(value: string): number {
    return parseFloat(String(value).replace(/[^\d.,]/g, "").replace(",", ".")) || 0;
}

export const STARTER_PACK_META: StarterPackScenarioMeta[] = [
    {
        scenarioId: 1,
        icon: "notify",
        description: "Напоминание о записи, подтверждение и уведомление об отмене. Автоматически",
        settingsType: "battle",
    },
    {
        scenarioId: 2,
        icon: "notify",
        description: "Уведомляет управляющего, если бот потерял связь или канал отправки отключился",
        settingsType: "deauth",
    },
    {
        scenarioId: 3,
        icon: "cascade",
        description: "Подключи каналы отправки (TG / WA / MAX) и расставь их приоритет — сделай это первым",
        settingsType: "cascade",
        redirectAnchor: "channels-section",
        drawerDescription:
            "Подключите каналы отправки и расставьте их приоритет. Это первый шаг — остальные сценарии разблокируются после подключения.",
    },
    {
        scenarioId: 4,
        icon: "report",
        description: "Ежедневный или еженедельный отчёт с ключевыми метриками в Telegram, WhatsApp или MAX",
        settingsType: "redirect-info",
        redirectPath: "scheduledReport?mode=create",
        drawerSectionTitle: "Состав, расписание и получатели",
        drawerSectionDescription:
            "Выберите метрики, периодичность и получателей отчёта в разделе «Отправка отчётов».",
        drawerLinkLabel: "Перейти в «Отправка отчётов»",
    },
    {
        scenarioId: 5,
        icon: "msg",
        description: "Выберите канал и подключите по нему трансляцию переписки администраторов.",
        settingsType: "comm",
    },
    {
        scenarioId: 6,
        icon: "invite",
        description: "Клиенты, которые были один раз 25-30 дней назад и больше не записывались",
        settingsType: "script",
        vars: ["{имя-клиента}", "{название-салона}", "{название-подарка}"],
        manualNote:
            "«Название подарка» нужно вписать вручную перед запуском - подставьте свой подарок к следующему визиту.",
        noEmployeePicker: true,
    },
    {
        scenarioId: 7,
        icon: "hot",
        description: "Ежедневный список клиентов, готовых записаться прямо сейчас",
        settingsType: "script",
        scriptOnly: true,
        noEmployeePicker: true,
    },
    {
        scenarioId: 14,
        icon: "forgot",
        description:
            "Должны были вернуться 2 недели – 2 месяца назад, но не записались снова. Предлагаем скидку 10%",
        settingsType: "script",
        scriptOnly: true,
        noEmployeePicker: true,
    },
    {
        scenarioId: 15,
        icon: "hot",
        description: "Должны были прийти от 2 до 4 месяцев назад, но не пришли.",
        settingsType: "script",
        vars: ["{имя-клиента}", "{название-салона}", "{название-подарка}"],
        manualNote:
            "«Название подарка» нужно вписать вручную перед запуском - подставьте свой подарок для возвращения клиента.",
        noEmployeePicker: true,
    },
    {
        scenarioId: 8,
        icon: "review",
        description: "Запрашивает отзыв после визита к недавно нанятому мастеру",
        settingsType: "script",
        scriptOnly: true,
        noEmployeePicker: true,
    },
    {
        scenarioId: 9,
        icon: "vacation",
        description: "Предупреждаем клиентов об отпуске мастера и предлагаем запись",
        settingsType: "script",
        vars: ["{имя-клиента}", "{ссылка-на-онлайн-запись}"],
        manualNote:
            "Имя мастера и даты отпуска («ИМЯ МАСТЕРА», «с 99 по 99 месяц») нужно вписать вручную перед запуском.",
    },
    {
        scenarioId: 10,
        icon: "invite",
        description: "Предлагаем записаться снова клиентам, отменившим запись от 2 до 7 дней назад.",
        settingsType: "script",
        scriptOnly: true,
        noEmployeePicker: true,
    },
    {
        scenarioId: 12,
        icon: "msg",
        description: "Напоминает мастеру детали клиента за 15 минут до начала визита",
        settingsType: "master15",
        vars: [
            "{имя-клиента}",
            "{дата-и-время}",
            "{названия-услуг}",
            "{цикл-возврата}",
            "{дополнительные-услуги}",
            "{товары}",
        ],
    },
    {
        scenarioId: 13,
        icon: "report",
        description: "Сводка записей и показателей мастера в конце дня",
        settingsType: "redirect-info",
        redirectPath: "scheduledReport?mode=create",
        drawerSectionTitle: "Состав и расписание отчёта",
        drawerSectionDescription:
            "Выберите метрики и время отправки отчёта мастеру в разделе «Отправка отчётов».",
        drawerLinkLabel: "Перейти к добавлению отчёта",
    },
];

export const mockStarterPackStatus: StarterPackStatusResponse = {
    scenarios: [
        {
            scenarioId: 1,
            name: "Боевой бот - сервисные уведомления",
            role: "manager",
            section: "must",
            action: "setBattleBotMode",
            status: "inactive",
            configured: false,
            settings: { mode: "test" },
        },
        {
            scenarioId: 2,
            name: "Сообщение о разрыве авторизации",
            role: "manager",
            section: "must",
            action: "applyDeauthContacts",
            status: "inactive",
            configured: false,
            settings: null,
        },
        {
            scenarioId: 3,
            name: "Подключение каналов и каскада",
            role: "manager",
            section: "must",
            action: "redirect",
            status: "inactive",
            configured: false,
            settings: null,
        },
        {
            scenarioId: 4,
            name: "Отчёт руководителю",
            role: "manager",
            section: "must",
            action: "redirect",
            status: "inactive",
            configured: false,
            settings: null,
        },
        {
            scenarioId: 5,
            name: "Контроль коммуникации",
            role: "manager",
            section: "must",
            action: "applyCommControl",
            status: "inactive",
            configured: false,
            settings: { phones: [] },
        },
        {
            scenarioId: 6,
            name: "Приглашение на 2-й визит (1П)",
            role: "admin",
            section: "quickstart-admin",
            action: "copyActivation",
            status: "inactive",
            configured: false,
            settings: {
                scriptText:
                    "{имя-клиента}, здравствуйте!\nСпасибо, что выбрали {название-салона}.\nХотим познакомить Вас со всеми нашими услугами.\n\nКстати, к следующему посещению мы дарим Вам {название-подарка}.\n\nПодскажите, планируете ли к нам в ближайшее время?",
                visitsFrom: 1,
                visitsTo: 1,
                daysFrom: 25,
                daysTo: 30,
            },
        },
        {
            scenarioId: 7,
            name: "Горячие клиенты - рассылка приглашений",
            role: "admin",
            section: "quickstart-admin",
            action: "copyActivation",
            automail: true,
            status: "inactive",
            configured: false,
            settings: {
                scriptText:
                    "С момента последнего визита прошло немного времени —\nхотим бережно напомнить: возможно, уже пора записаться ❤️\n\nУточните, пожалуйста, удобно ли подобрать время и услугу?",
            },
        },
        {
            scenarioId: 14,
            name: "Клиенты, которые забыли прийти",
            role: "admin",
            section: "quickstart-admin",
            action: "copyActivation",
            automail: true,
            status: "inactive",
            configured: false,
            settings: {
                scriptText:
                    "Возможно сейчас у Вас появилось время на нас 🥰\nСегодня при записи действует скидка 10% на любую процедуру.",
            },
        },
        {
            scenarioId: 15,
            name: "Прохладные клиенты",
            role: "admin",
            section: "quickstart-admin",
            action: "copyActivation",
            automail: true,
            status: "inactive",
            configured: false,
            settings: {
                daysFrom: 61,
                daysTo: 120,
                scriptText:
                    "Мы заметили, что Вас давно не было и хотим пригласить снова -\nв знак внимания дарим Вам подарок {название-подарка}.",
            },
        },
        {
            scenarioId: 8,
            name: "Отзыв по новому мастеру",
            role: "manager",
            section: "quickstart-admin",
            action: "copyActivation",
            automail: true,
            status: "inactive",
            configured: false,
            settings: {
                scriptText:
                    "Ваше последнее посещение было у нового для Вас мастера. Подскажите, все ли Вам понравилось? Есть ли замечания? Хотите ли Вы вернуть прежнего мастера?",
            },
        },
        {
            scenarioId: 9,
            name: "Отпуск мастера",
            role: "manager",
            section: "quickstart-admin",
            action: "copyActivation",
            automail: true,
            status: "inactive",
            configured: false,
            settings: {
                scriptText:
                    "{имя-клиента}, добрый день!\nВаш специалист ИМЯ МАСТЕРА уходит в отпуск:\nс 99 месяц по 99 месяц.\n\nРекомендуем записаться заранее - места в последние дни перед отпуском разбирают быстро.\n\nПодобрать для Вас удобное время?\n\nЗаписаться самостоятельно:\n{ссылка-на-онлайн-запись}",
                visitsFrom: 3,
                visitsTo: 1000,
                masterIds: [],
            },
        },
        {
            scenarioId: 10,
            name: "Запись отмененных",
            role: "admin",
            section: "quickstart-admin",
            action: "copyActivation",
            automail: true,
            status: "inactive",
            configured: false,
            settings: {
                scriptText:
                    "Заметили, что запись не состоялась - будем рады видеть Вас снова 🌿\nПодобрать удобное время?",
            },
        },
        {
            scenarioId: 12,
            name: "Сообщение мастеру за 15 минут",
            role: "master",
            section: "quickstart-master",
            action: "copyActivation",
            status: "inactive",
            configured: false,
            settings: {
                enabled: true,
                scriptText:
                    "Совсем скоро к вам придет клиент: {имя-клиента}\n\nОн записан на {дата-и-время} на услуги:\n{названия-услуг}\n\n{имя-клиента} посещает салон раз в {цикл-возврата}\n\nНесколько раз пробовал дополнительные услуги:\n{дополнительные-услуги}\n\n{имя-клиента} уже покупал у нас:\n{товары}\n\nОбязательно предложите клиенту домашний уход и/или порекомендуйте дополнительные услуги во время записи.\n\nЛегкой работы и высоких чеков!",
                additionalServiceIds: ["svc_001", "svc_003"],
            },
        },
        {
            scenarioId: 13,
            name: "Ежедневный отчёт мастеру",
            role: "master",
            section: "quickstart-master",
            action: "redirect",
            status: "inactive",
            configured: false,
            settings: null,
        },
    ],
};

export const mockInfoCards: StarterPackInfoCard[] = [
    {
        icon: "stoplist",
        name: "Автостоп-лист",
        description: "Автоматически добавляет в список «Не напоминаем» клиентов, которые попросили им не писать",
        noLearn: true,
    },
    {
        icon: "cohort",
        name: "Возвращаемость новых клиентов",
        description: "Какая доля новых клиентов возвращается после первого визита в разрезе каждого специалиста",
        learnPath: "GCB2/marketingReport?type=staff",
    },
    {
        icon: "base",
        name: "Управление клиентской базой",
        description: "Внедряем систему, которая учитывает ВСЕ клиентские сценарии",
        pro: true,
        marketplaceModule: "crmSettings",
    },
];

export const mockServices: StarterPackService[] = [
    { id: "svc_001", name: "Уход за волосами", category: "Парикмахерский зал" },
    { id: "svc_002", name: "Ботокс для волос", category: "Парикмахерский зал" },
    { id: "svc_003", name: "SPA-уход для волос", category: "Парикмахерский зал" },
    { id: "svc_004", name: "Массаж головы", category: "Парикмахерский зал" },
    { id: "svc_005", name: "Парафинотерапия рук", category: "Ногтевой сервис" },
    { id: "svc_006", name: "Укрепление ногтей", category: "Ногтевой сервис" },
    { id: "svc_007", name: "Пилинг лица", category: "Кабинет косметолога" },
    { id: "svc_008", name: "Ламинирование бровей", category: "Бровист" },
];

export const mockStaff: StarterPackStaff[] = [
    { id: "staff_001", name: "Анна Ковалёва" },
    { id: "staff_002", name: "Игорь Петров" },
    { id: "staff_003", name: "Мария Седова" },
    { id: "staff_004", name: "Дмитрий Лосев" },
    { id: "staff_005", name: "Елена Власова" },
    { id: "staff_006", name: "Ольга Зайцева" },
];

export const hardcodedSuccessCases: StarterPackCase[] = [
    {
        metric: "Конверсия в повторный визит",
        up: "+14%",
        was: "21%",
        now: "35%",
        bars: [40, 52, 64, 80, 100],
        caption: "Клиника «МедЛайн» · новые пациенты · за 6 недель",
    },
    {
        metric: "Прирост выручки за месяц",
        up: "+19%",
        was: "₽640 тыс",
        now: "₽762 тыс",
        bars: [44, 55, 67, 82, 100],
        caption: "Ногтевая студия «Lacqré» · помесячно · за 2 месяца",
    },
    {
        metric: "Конверсия в сегменте «Стрижка+борода»",
        up: "+11%",
        was: "33%",
        now: "44%",
        bars: [55, 64, 74, 86, 100],
        caption: "Барбершоп «Bruno» · сегмент · за 5 недель",
    },
    {
        metric: "Конверсия с 1 до 2 визита",
        up: "+17%",
        was: "16%",
        now: "33%",
        bars: [34, 46, 58, 74, 100],
        caption: "Салон красоты «Aura» · новые клиенты · за 6 недель",
    },
    {
        metric: "Прирост выручки за месяц",
        up: "+23%",
        was: "₽1,72 млн",
        now: "₽2,12 млн",
        bars: [40, 50, 63, 80, 100],
        caption: "Клиника «Доктор Плюс» · помесячно · за 2 месяца",
    },
    {
        metric: "Конверсия в повторный визит",
        up: "+9%",
        was: "38%",
        now: "47%",
        bars: [60, 70, 80, 90, 100],
        caption: "Ногтевая студия «Pink» · новые клиенты · за 4 недели",
    },
    {
        metric: "Прирост выручки за месяц",
        up: "+15%",
        was: "₽980 тыс",
        now: "₽1,13 млн",
        bars: [48, 60, 72, 85, 100],
        caption: "Барбершоп «Старый Лис» · помесячно · за 2 месяца",
    },
    {
        metric: "Конверсия в сегменте «Окрашивание»",
        up: "+12%",
        was: "27%",
        now: "39%",
        bars: [52, 62, 73, 85, 100],
        caption: "Салон красоты «Loft» · сегмент · за 5 недель",
    },
    {
        metric: "Конверсия с 1 до 2 визита",
        up: "+21%",
        was: "12%",
        now: "33%",
        bars: [28, 42, 58, 76, 100],
        caption: "Клиника «Эстетика» · новые пациенты · за 7 недель",
    },
    {
        metric: "Прирост выручки за месяц",
        up: "+13%",
        was: "₽540 тыс",
        now: "₽610 тыс",
        bars: [50, 62, 73, 86, 100],
        caption: "Ногтевая студия «Glossy» · помесячно · за 6 недель",
    },
    {
        metric: "Конверсия в повторный визит",
        up: "+18%",
        was: "29%",
        now: "47%",
        bars: [42, 54, 68, 83, 100],
        caption: "Барбершоп «Bro» · новые клиенты · за 6 недель",
    },
    {
        metric: "Прирост выручки за месяц",
        up: "+25%",
        was: "₽2,1 млн",
        now: "₽2,63 млн",
        bars: [38, 50, 64, 81, 100],
        caption: "Салон красоты «Vesna» · помесячно · за 2 месяца",
    },
    {
        metric: "Конверсия в сегменте «Косметология»",
        up: "+10%",
        was: "34%",
        now: "44%",
        bars: [57, 66, 76, 87, 100],
        caption: "Клиника «Семейная» · сегмент · за 5 недель",
    },
    {
        metric: "Конверсия с 1 до 2 визита",
        up: "+16%",
        was: "18%",
        now: "34%",
        bars: [36, 48, 60, 77, 100],
        caption: "Ногтевая студия «Nails&Co» · новые клиенты · за 6 недель",
    },
    {
        metric: "Прирост выручки за месяц",
        up: "+8%",
        was: "₽760 тыс",
        now: "₽821 тыс",
        bars: [60, 71, 81, 90, 100],
        caption: "Барбершоп «Топор» · помесячно · за 5 недель",
    },
    {
        metric: "Конверсия в повторный визит",
        up: "+22%",
        was: "25%",
        now: "47%",
        bars: [34, 48, 63, 80, 100],
        caption: "Салон красоты «Шик» · новые клиенты · за 7 недель",
    },
    {
        metric: "Прирост выручки за месяц",
        up: "+17%",
        was: "₽1,35 млн",
        now: "₽1,58 млн",
        bars: [46, 58, 70, 84, 100],
        caption: "Клиника «Вита» · помесячно · за 2 месяца",
    },
    {
        metric: "Конверсия в сегменте «Маникюр»",
        up: "+7%",
        was: "41%",
        now: "48%",
        bars: [64, 73, 82, 91, 100],
        caption: "Ногтевая студия «Мани» · сегмент · за 4 недели",
    },
];

export const hardcodedReviews: StarterPackReview[] = [
    {
        initials: "ИК",
        avatarColor: "#E8743B",
        name: "Ирина",
        outbound:
            "Ирина, здравствуйте! Спасибо, что выбрали «Aspecto» 💛 К следующему визиту дарим уход за волосами 🎁 Планируете зайти?",
        outboundTime: "12:04",
        inbound: "Ой, как приятно! Да, давно собиралась. Запишите на пятницу вечером, пожалуйста 💛",
        inboundTime: "12:31",
        tag: "Приглашение на 2-й визит",
        heart: true,
    },
    {
        initials: "СМ",
        avatarColor: "#5C6BC0",
        name: "Светлана",
        outbound: "Светлана, добрый день! Ваш мастер Анна уходит в отпуск с 12 по 26 августа. Записать вас заранее?",
        outboundTime: "10:18",
        inbound: "Спасибо, что предупредили! Да, давайте на этой неделе, не хочу ждать 🙏",
        inboundTime: "10:25",
        tag: "Отпуск мастера",
        heart: true,
    },
    {
        initials: "ДВ",
        avatarColor: "#2E9E6B",
        name: "Дарья",
        inbound:
            "Спасибо огромное за напоминание и за подарок! Очень приятно, что о клиентах помнят. Обязательно приду ещё ✨",
        inboundTime: "19:42",
        tag: "Сервисные уведомления",
        heart: true,
    },
    {
        initials: "НП",
        avatarColor: "#9C5BC0",
        name: "Наталья",
        outbound:
            "Наталья, добрый день! Видим, вы давно у нас не были 🙂 Соскучились! Подобрать удобное время для записи?",
        outboundTime: "14:09",
        inbound: "Точно, закрутилась совсем. Спасибо, что напомнили — давайте на субботу!",
        inboundTime: "14:21",
        tag: "Клиенты, которые забыли прийти",
        heart: true,
    },
    {
        initials: "АГ",
        avatarColor: "#0E9DB5",
        name: "Алексей",
        outbound: "Алексей, напоминаем: завтра в 18:00 вы записаны к барберу Олегу. Всё в силе?",
        outboundTime: "17:30",
        inbound: "Да, подтверждаю, буду 👍 удобно, что напомнили",
        inboundTime: "17:34",
        tag: "Сервисные уведомления",
        heart: false,
    },
    {
        initials: "ЕР",
        avatarColor: "#D2477E",
        name: "Екатерина",
        outbound: "Екатерина, как прошёл визит к новому мастеру Марине? Оцените, пожалуйста — нам важно ваше мнение ⭐",
        outboundTime: "11:50",
        inbound: "Всё супер! Марина — золотые руки, маникюр держится отлично. 5 из 5 ❤️",
        inboundTime: "12:15",
        tag: "Отзыв по новому мастеру",
        heart: true,
    },
    {
        initials: "ОЛ",
        avatarColor: "#3F7FE0",
        name: "Ольга",
        outbound: "Ольга, здравствуйте! Сейчас как раз есть удобные окошки на эту неделю. Хотите записаться?",
        outboundTime: "09:40",
        inbound: "Да, давно хотела! Запишите на четверг, если можно днём 🙌",
        inboundTime: "09:52",
        tag: "Горячие клиенты",
        heart: true,
    },
    {
        initials: "ММ",
        avatarColor: "#E0843B",
        name: "Марина",
        outbound: "Марина, добрый день! К сожалению, ваш сеанс пришлось отменить. Давайте подберём новое время?",
        outboundTime: "13:12",
        inbound: "Спасибо за заботу! Да, перенесите на следующую неделю, пожалуйста",
        inboundTime: "13:20",
        tag: "Запись отмененных",
        heart: false,
    },
    {
        initials: "ТС",
        avatarColor: "#5BA86B",
        name: "Татьяна",
        inbound:
            "Очень удобно, что напоминаете и предлагаете запись заранее. Чувствуешь, что о тебе действительно заботятся 💛",
        inboundTime: "20:05",
        tag: "Сервисные уведомления",
        heart: true,
    },
];

export function getMockStarterPackStatus(): Promise<StarterPackStatusResponse> {
    return Promise.resolve(JSON.parse(JSON.stringify(mockStarterPackStatus)));
}
